import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { getBetLoopSearchProfiles } from '@core/Areas/BetLoop/helpers/api/index'
import { BetLoopFeedPageSize } from '@core/Areas/BetLoop/constants'
import { ProfileSortOrder } from '@core/Areas/BetLoop/Components/SearchProfiles/types'

export const useSearchProfiles = (
  searchTerm?: string | undefined,
  sortOrder?: ProfileSortOrder
) => {
  const search = searchTerm?.trim()
  const order = !search && sortOrder ? sortOrder : 'FollowerCount'
  const validRegex = /^[a-zA-Z0-9\s.,'"-]*$/
  const isValidSearch = search && validRegex.test(search)

  const sortQuery = useInfiniteQuery({
    queryKey: [
      ...queryKeys.betLoopAccountExploreProfiles,
      { pageSize: BetLoopFeedPageSize, searchQuery: '', sortOrder: order },
    ],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
      return getBetLoopSearchProfiles({
        pageSize: BetLoopFeedPageSize,
        pageParam,
        sortOrder: order,
      })
    },

    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      if (!lastPage || lastPage.length < Number(BetLoopFeedPageSize)) return undefined
      return lastPage[lastPage.length - 1]?.id ?? undefined
    },

    staleTime: 1_000 * 60 * 2, // 2min
    gcTime: 1_000 * 60 * 5,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  })

  const searchQuery = useInfiniteQuery({
    queryKey: [
      ...queryKeys.betLoopAccountExploreProfiles,
      { pageSize: BetLoopFeedPageSize, searchQuery: search ?? '', sortOrder: 'FollowerCount' },
    ],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
      return getBetLoopSearchProfiles({
        pageSize: BetLoopFeedPageSize,
        searchQuery: search,
        pageParam,
        sortOrder: 'FollowerCount',
      })
    },

    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      if (!lastPage || lastPage.length < Number(BetLoopFeedPageSize)) return undefined
      return lastPage[lastPage.length - 1]?.id ?? undefined
    },

    staleTime: 1_000 * 60 * 2, // 2min
    gcTime: 1_000 * 60 * 5,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    enabled: isValidSearch || !!search,
  })

  const searchIsEmpty =
    isValidSearch && !searchQuery.isLoading && searchQuery.data?.pages[0]?.length === 0

  const query = isValidSearch && !searchIsEmpty ? searchQuery : sortQuery

  const isFinalPage =
    (query?.data?.pages?.[query?.data.pages.length - 1]?.length ?? 0) < BetLoopFeedPageSize

  return {
    data: query?.data,
    isLoading: query?.isLoading,
    isRefetching: query?.isRefetching,
    isEmptyState: searchIsEmpty,
    isValidSearch,
    LoadMoreButton: () =>
      isFinalPage || searchIsEmpty ? null : (
        <AutoFetchButton onClick={query?.fetchNextPage} isFetching={query?.isFetchingNextPage} />
      ),
  }
}
